import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Clock, Crown } from "lucide-react";
import { motion } from "framer-motion";

const CATEGORY_LABEL = { restaurant: "Restaurant", bar: "Bar", cafe: "Café", activity: "Activity", nightlife: "Nightlife", event: "Event" };

export function isOpenNow(hours) {
  if (!hours) return null;
  // Compute current weekday + hour:minute in Australia/Brisbane time (UTC+10, no DST)
  // This way, viewers in any timezone see the correct open/closed state for the venue.
  const fmt = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Brisbane",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date()).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const dayMap = { Mon: "mon", Tue: "tue", Wed: "wed", Thu: "thu", Fri: "fri", Sat: "sat", Sun: "sun" };
  const day = dayMap[parts.weekday];
  if (!day) return null;
  let spec = hours[day];
  if (spec === undefined || spec === null || spec === "") return null;
  // Multi-period support: "11:30-14:00,17:30-21:30" — open if any segment matches
  const segments = String(spec).split(",").map((s) => s.trim()).filter(Boolean);
  const nowMin = parseInt(parts.hour, 10) * 60 + parseInt(parts.minute, 10);
  for (const seg of segments) {
    if (seg.toLowerCase() === "24h") return true;
    try {
      const [s, e] = seg.split("-");
      const [sh, sm] = s.split(":").map(Number);
      const [eh, em] = e.split(":").map(Number);
      const startMin = sh * 60 + sm;
      let endMin = eh * 60 + em;
      if (endMin <= startMin) {
        if (nowMin >= startMin || nowMin <= endMin) return true;
      } else {
        if (nowMin >= startMin && nowMin <= endMin) return true;
      }
    } catch { /* try next segment */ }
  }
  return false;
}

// Returns the closing time string ("3am", "10pm", "midnight") of the current open segment, or null if closed.
export function closingTimeLabel(hours) {
  if (!hours) return null;
  const fmt = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Brisbane",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date()).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const dayMap = { Mon: "mon", Tue: "tue", Wed: "wed", Thu: "thu", Fri: "fri", Sat: "sat", Sun: "sun" };
  const day = dayMap[parts.weekday];
  if (!day) return null;
  const spec = hours[day];
  if (!spec) return null;
  const segments = String(spec).split(",").map((s) => s.trim()).filter(Boolean);
  const nowMin = parseInt(parts.hour, 10) * 60 + parseInt(parts.minute, 10);
  const fmtTime = (h, m) => {
    if (h === 0 && m === 0) return "midnight";
    if (h === 12 && m === 0) return "noon";
    const ampm = h < 12 ? "am" : "pm";
    const hr12 = h % 12 === 0 ? 12 : h % 12;
    return m === 0 ? `${hr12}${ampm}` : `${hr12}:${String(m).padStart(2, "0")}${ampm}`;
  };
  for (const seg of segments) {
    if (seg.toLowerCase() === "24h") return "24h";
    try {
      const [s, e] = seg.split("-");
      const [sh, sm] = s.split(":").map(Number);
      const [eh, em] = e.split(":").map(Number);
      const startMin = sh * 60 + sm;
      const endMin = eh * 60 + em;
      const inSegment = endMin <= startMin
        ? (nowMin >= startMin || nowMin <= endMin)
        : (nowMin >= startMin && nowMin <= endMin);
      if (inSegment) return fmtTime(eh, em);
    } catch { /* skip */ }
  }
  return null;
}

// Returns a label like "Opens 5pm" or "Opens Mon 9am" for a venue that's
// currently closed. Looks up to 7 days ahead. null if hours are unknown/empty.
export function nextOpenLabel(hours) {
  if (!hours) return null;
  const fmt = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Brisbane",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date()).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const shortDays = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const shortDaysLabel = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const dayIdx = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const todayIdx = dayIdx[parts.weekday];
  if (todayIdx === undefined) return null;
  const nowMin = parseInt(parts.hour, 10) * 60 + parseInt(parts.minute, 10);

  const fmtTime = (h, m) => {
    if (h === 0 && m === 0) return "midnight";
    if (h === 12 && m === 0) return "noon";
    const ampm = h < 12 ? "am" : "pm";
    const hr12 = h % 12 === 0 ? 12 : h % 12;
    return m === 0 ? `${hr12}${ampm}` : `${hr12}:${String(m).padStart(2, "0")}${ampm}`;
  };

  // Scan today first (for later-today openings), then next 6 days.
  for (let offset = 0; offset < 7; offset++) {
    const idx = (todayIdx + offset) % 7;
    const spec = hours[shortDays[idx]];
    if (!spec) continue;
    if (String(spec).toLowerCase() === "24h") {
      return offset === 0 ? "Opens soon" : `Opens ${shortDaysLabel[idx]}`;
    }
    const segments = String(spec).split(",").map((s) => s.trim()).filter(Boolean);
    for (const seg of segments) {
      const [s] = seg.split("-");
      if (!s) continue;
      const [sh, sm] = s.split(":").map(Number);
      if (Number.isNaN(sh) || Number.isNaN(sm)) continue;
      const startMin = sh * 60 + sm;
      // For today, only consider start times still in the future.
      if (offset === 0 && startMin <= nowMin) continue;
      const label = fmtTime(sh, sm);
      return offset === 0 ? `Opens ${label}` : `Opens ${shortDaysLabel[idx]} ${label}`;
    }
  }
  return null;
}

export default function VenueCard({ venue, index = 0 }) {
  const img = venue.places_photo_url || venue.images?.[0] || "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200";
  const open = isOpenNow(venue.hours);
  const closing = open ? closingTimeLabel(venue.hours) : null;
  const nextOpen = open ? null : nextOpenLabel(venue.hours);
  const isSponsored = venue.is_sponsored;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      data-testid={`venue-card-${venue.id}`}
    >
      <Link
        to={`/venue/${venue.id}`}
        className={`card-hover group block rounded-2xl overflow-hidden border ${isSponsored ? "border-sand bg-[#FDFBF7]" : "border-black/5 bg-white"} shadow-sm`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-sand/30">
          <img
            src={img}
            alt={venue.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {isSponsored && (
            <div className="absolute top-3 left-3 sponsored-ribbon px-3 py-1 rounded-full text-xs flex items-center gap-1" data-testid="sponsored-badge">
              <Crown className="w-3 h-3" /> Sponsored
            </div>
          )}
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-xs font-medium text-navy">
            {CATEGORY_LABEL[venue.category] || venue.category}
          </div>
          {open !== null && (
            <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5" data-testid={`open-pill-${venue.id}`}>
              <span className={`w-2 h-2 rounded-full ${open ? "bg-emerald-500" : "bg-rose-400"}`} />
              <span className="text-neutral-700">
                {open
                  ? (closing && closing !== "24h" ? `Open until ${closing}` : (closing === "24h" ? "Open 24h" : "Open now"))
                  : (nextOpen || "Closed")}
              </span>
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h3 className="font-heading text-lg text-navy line-clamp-1" data-testid={`venue-title-${venue.id}`}>{venue.name}</h3>
            <div className="flex items-center gap-1 shrink-0">
              <Star className="w-4 h-4 fill-sponsored text-sponsored" />
              <span className="text-sm font-medium">{(venue.rating || 0).toFixed(1)}</span>
            </div>
          </div>
          <p className="text-sm text-neutral-500 line-clamp-2 mb-3 leading-relaxed">{venue.description}</p>
          <div className="flex items-center justify-between text-xs text-neutral-500">
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {venue.location}</span>
            <span className="text-neutral-400">{venue.review_count || 0} reviews</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
